import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Clock, AlertTriangle } from 'lucide-react';

interface SlaTimerProps {
  deadline: string | null;
  violated: boolean | null;
  size?: 'sm' | 'md';
  className?: string;
}

function computeSla(deadline: string | null, violated: boolean | null) {
  if (violated) return { label: 'VIOLADO', color: 'destructive' as const };
  if (!deadline) return { label: 'Sem SLA', color: 'muted' as const };

  const now = Date.now();
  const dl = new Date(deadline).getTime();
  const diffMs = dl - now;

  if (diffMs <= 0) return { label: 'VIOLADO', color: 'destructive' as const };

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const label = hours > 0 ? `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}` : `${minutes}m`;
  const color = totalMinutes <= 120 ? ('warning' as const) : ('ok' as const);

  return { label, color };
}

const COLOR_MAP = {
  ok: 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30',
  warning: 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30',
  destructive: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30',
  muted: 'text-muted-foreground bg-muted',
};

export function SlaTimer({ deadline, violated, size = 'sm', className }: SlaTimerProps) {
  const [sla, setSla] = useState(() => computeSla(deadline, violated));

  useEffect(() => {
    setSla(computeSla(deadline, violated));
    const interval = setInterval(() => {
      setSla(computeSla(deadline, violated));
    }, 60_000);
    return () => clearInterval(interval);
  }, [deadline, violated]);

  const Icon = sla.color === 'destructive' ? AlertTriangle : Clock;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium',
        COLOR_MAP[sla.color],
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
        className
      )}
    >
      <Icon className={cn(size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
      {sla.label}
    </span>
  );
}
